import { memo, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShoppingBagIcon, CheckCircleIcon, TrashIcon } from '@heroicons/react/24/outline'
import { adminAPI } from '../../services/api'
import { toast } from '../../services/toastService'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import useAdminTableState from '../../hooks/useAdminTableState'
import AdminTableControls from './AdminTableControls'

const CATEGORY_COLORS = {
  food:       'bg-orange-100 text-orange-700',
  goods:      'bg-indigo-100 text-indigo-700',
  service:    'bg-teal-100 text-teal-700',
  mutual_aid: 'bg-amber-100 text-amber-700',
}

const AdminListingsSection = memo(function AdminListingsSection({ onBack }) {
  const navigate = useNavigate()
  const { execute } = useAsyncOperation()
  const [activeTab, setActiveTab] = useState('listings') // 'listings' | 'requests'
  const [selectedIds, setSelectedIds] = useState(new Set())

  const {
    data: listings, loading, error,
    searchInput, filter, pagination,
    handleSearchChange, handleFilterChange, handlePageChange, refetch,
  } = useAdminTableState(
    async (params) => execute(async () => {
      const apiParams = { ...params }
      if (params.filter && params.filter !== 'all') { apiParams.status = params.filter; delete apiParams.filter }
      const res = await adminAPI.getAdminListings(apiParams)
      return {
        data: res.data?.listings || [],
        pagination: {
          page: res.data?.pagination?.page || 1,
          totalPages: res.data?.pagination?.totalPages || 1,
          total: res.data?.pagination?.total || 0,
        },
      }
    }),
    { pageSize: 50 }
  )

  const {
    data: requests, loading: reqLoading,
    filter: reqFilter, handleFilterChange: handleReqFilterChange,
    refetch: refetchReqs,
  } = useAdminTableState(
    async (params) => execute(async () => {
      const apiParams = { ...params }
      if (params.filter && params.filter !== 'all') { apiParams.status = params.filter; delete apiParams.filter }
      const res = await adminAPI.getAdminListingRequests(apiParams)
      return { data: res.data?.requests || [], pagination: { page: 1, totalPages: 1, total: res.data?.requests?.length || 0 } }
    }),
    { pageSize: 200 }
  )

  const handleFulfill = useCallback(async (id) => {
    if (!confirm('Mark this listing as fulfilled?')) return
    await execute(() => adminAPI.adminFulfillListing(id), {
      onSuccess: () => { toast.success('Listing marked as fulfilled'); refetch() },
      onError: () => toast.error('Failed to update listing'),
    })
  }, [execute, refetch])

  const handleDelete = useCallback(async (id) => {
    if (!confirm('Permanently delete this listing?')) return
    await execute(() => adminAPI.adminDeleteListing(id), {
      onSuccess: () => { toast.success('Listing deleted'); refetch() },
      onError: () => toast.error('Failed to delete listing'),
    })
  }, [execute, refetch])

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return
    if (!confirm(`Delete ${selectedIds.size} listing(s)?`)) return
    await execute(async () => {
      await Promise.all(Array.from(selectedIds).map(id => adminAPI.adminDeleteListing(id)))
      setSelectedIds(new Set())
      refetch()
    }, { successMessage: `${selectedIds.size} listing(s) deleted` })
  }, [selectedIds, execute, refetch])

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s })
  }, [])

  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'expired', label: 'Expired' },
    { value: 'fulfilled', label: 'Fulfilled' },
  ]

  const reqFilterOptions = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'declined', label: 'Declined' },
  ]

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-4 lg:px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-gray-500 hover:text-gray-700 text-sm">← Back</button>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <ShoppingBagIcon className="w-6 h-6 text-amber-500" />
            Listings Management
          </h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('listings')}
            onTouchEnd={() => setActiveTab('listings')}
            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${activeTab === 'listings' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            Listings
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            onTouchEnd={() => setActiveTab('requests')}
            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${activeTab === 'requests' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            Requests
          </button>
        </div>
      </div>

      {activeTab === 'listings' && (
        <>
          <AdminTableControls
            searchInput={searchInput}
            onSearchChange={handleSearchChange}
            filter={filter}
            onFilterChange={handleFilterChange}
            filterOptions={filterOptions}
            pagination={pagination}
            onPageChange={handlePageChange}
            loading={loading}
          />
          {selectedIds.size > 0 && (
            <div className="px-4 py-2 bg-red-50 flex items-center gap-3">
              <span className="text-sm text-red-700">{selectedIds.size} selected</span>
              <button onClick={handleBulkDelete} className="text-sm text-red-600 font-medium hover:underline">Delete selected</button>
              <button onClick={() => setSelectedIds(new Set())} className="text-sm text-gray-500 hover:underline">Clear</button>
            </div>
          )}
          {error && <div className="px-4 py-3 text-sm text-red-600">{error.message || 'Failed to load listings'}</div>}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left"><input type="checkbox" onChange={(e) => e.target.checked ? setSelectedIds(new Set(listings.map(l => l.id))) : setSelectedIds(new Set())} /></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Posted by</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {listings.map((l) => {
                  const expired = new Date(l.expires_at) < new Date()
                  const statusLabel = l.is_fulfilled ? 'Fulfilled' : expired ? 'Expired' : 'Active'
                  const statusColor = l.is_fulfilled ? 'bg-gray-100 text-gray-600' : expired ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'
                  return (
                    <tr key={l.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3"><input type="checkbox" checked={selectedIds.has(l.id)} onChange={() => toggleSelect(l.id)} /></td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 text-sm">{l.title}</div>
                        {l.address && <div className="text-xs text-gray-400 mt-0.5">{l.address}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${CATEGORY_COLORS[l.category] || 'bg-gray-100 text-gray-600'}`}>
                          {l.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{l.display_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{l.price != null ? `$${l.price.toFixed(2)}` : 'Free'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{new Date(l.expires_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColor}`}>{statusLabel}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {!l.is_fulfilled && (
                            <button onClick={() => handleFulfill(l.id)} onTouchEnd={() => handleFulfill(l.id)} title="Mark fulfilled" className="text-green-600 hover:text-green-700">
                              <CheckCircleIcon className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => handleDelete(l.id)} onTouchEnd={() => handleDelete(l.id)} title="Delete" className="text-red-500 hover:text-red-700">
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {!loading && listings.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-400">No listings found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'requests' && (
        <>
          <div className="px-4 py-3 border-b border-gray-100 flex gap-2">
            {reqFilterOptions.map(o => (
              <button key={o.value} onClick={() => handleReqFilterChange(o.value)} onTouchEnd={() => handleReqFilterChange(o.value)}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${reqFilter === o.value ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                {o.label}
              </button>
            ))}
            <button onClick={refetchReqs} className="ml-auto text-xs text-primary hover:underline">Refresh</button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Listing</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requester</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.map((r) => {
                  const statusColor = r.status === 'accepted' ? 'bg-green-100 text-green-700' : r.status === 'declined' ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-700'
                  return (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{r.listing_title}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{r.requester_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{r.message}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColor}`}>{r.status}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">{new Date(r.created_at).toLocaleDateString()}</td>
                    </tr>
                  )
                })}
                {!reqLoading && requests.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">No requests found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
})

export default AdminListingsSection
